import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { plural } from '../formato';
import { Aviso, Carregando, ErroDaApi } from '../ui';
import { NomeDaAmostra, daApi, nomeDaAmostra } from '../NomeDaAmostra';

/**
 * Ligar às previstas — onda 250, `RN-219`.
 *
 * «Estamos com uma divergência de amostra já recebida da WSgeo que consta
 *  como "esperando amostra"» — Humberto, 02/10/2026 (OS 2610364).
 *
 * A amostra que chegou num lote sem OS ganha a OS depois, mas a ligação
 * com o que a OS PREVIU não vinha junto: a prevista ficava «por chegar»
 * com os ensaios marcados, e a recebida, sem ensaio nenhum. Aqui a pessoa
 * vê as recebidas soltas, a prevista que o nome sugere já escolhida, e
 * confirma. Os ensaios marcados na prevista vêm com a ligação.
 *
 * Duas portas, o mesmo componente:
 *  · o cartão «Esperando amostra» da lista de recebimentos (a OS inteira);
 *  · a ficha do recebimento, logo depois de atribuir a OS (só as amostras
 *    DESTE lote, e só quando há o que ligar).
 */

interface Sugestao {
  amostra_id: string; prevista_id: string;
}
interface Solta {
  id: string; codigo: string; identificacao_recebida: string | null;
  lote_codigo: string | null; lote_recebimento_id: string | null;
  ensaio_comecou: boolean;
}
interface Aberta {
  id: string; ordem: number; identificacao_prevista: string | null;
  origem_prevista: string | null; metodos: string[];
}
interface Ligacoes { sugestoes: Sugestao[]; soltas: Solta[]; abertas: Aberta[] }
interface Ligada {
  amostra_codigo: string; identificacao_prevista: string | null;
  herdados: string[]; nao_entraram: string[];
  /** 303: o nome do cliente, guardado antes de a amostra sair das soltas. */
  identificacao?: string | null;
}

const nomeDaPrevista = (p: Aberta) =>
  [p.identificacao_prevista, p.origem_prevista].filter(Boolean).join(' · ')
  || `Prevista ${p.ordem}`;

export function LigarPrevistas({
  osId, loteId, cartao = false, aoLigar,
}: {
  osId: string;
  /** Só as amostras deste lote (a ficha do recebimento). */
  loteId?: string;
  /** Dentro de um cartão próprio, que some quando não há o que ligar. */
  cartao?: boolean;
  aoLigar?(): void | Promise<void>;
}) {
  const [dados, setDados] = useState<Ligacoes | null>(null);
  const [escolha, setEscolha] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const [feitas, setFeitas] = useState<Ligada[] | null>(null);

  const carregar = useCallback(async () => {
    try {
      const d = await api<Ligacoes>(`/os/${osId}/ligacoes`);
      setDados(d);
      const inicial: Record<string, string> = {};
      for (const s of d.sugestoes) inicial[s.amostra_id] = s.prevista_id;
      setEscolha(inicial);
      setErro(null);
    } catch (e) { setErro(e); }
  }, [osId]);

  useEffect(() => { void carregar(); }, [carregar]);

  if (!dados) {
    if (cartao) return erro ? <ErroDaApi erro={erro} /> : null;
    return erro ? <ErroDaApi erro={erro} /> : <Carregando texto="Procurando o que ligar" />;
  }

  const soltas = loteId
    ? dados.soltas.filter((s) => s.lote_recebimento_id === loteId)
    : dados.soltas;
  const nada = soltas.length === 0 || dados.abertas.length === 0;

  if (nada && !feitas) {
    if (cartao) return null;
    return (
      <p className="vazio">
        {soltas.length === 0
          ? 'Todas as amostras recebidas desta OS já estão ligadas a uma prevista.'
          : 'A OS não tem prevista aberta para ligar: tudo o que ela previu já chegou.'}
      </p>
    );
  }

  const sugerida = new Map(dados.sugestoes.map((s) => [s.amostra_id, s.prevista_id]));
  const pares = Object.entries(escolha)
    .filter(([amostraId, previstaId]) => previstaId && soltas.some((s) => s.id === amostraId))
    .map(([amostraId, previstaId]) => ({ amostraId, previstaId }));

  async function ligar() {
    setErro(null); setOcupado(true);
    try {
      const r = await api<{ ligadas: Ligada[] }>(`/os/${osId}/ligacoes`, {
        metodo: 'POST', corpo: { pares },
      });
      // 303 (RN-248): a resposta só traz o código; o nome do cliente vem das
      // soltas, lido ANTES de recarregar (depois de ligada ela some delas).
      setFeitas(r.ligadas.map((f) => ({
        ...f,
        identificacao: soltas.find((s) => s.codigo === f.amostra_codigo)?.identificacao_recebida ?? null,
      })));
      await carregar();
      await aoLigar?.();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  }

  const corpo = (
    <>
      <ErroDaApi erro={erro} />
      {feitas && feitas.length > 0 && (
        <Aviso tipo="ok" titulo={`${plural(feitas.length, 'amostra ligada', 'amostras ligadas')} à prevista.`}>
          <ul className="ligadas">
            {feitas.map((f) => (
              <li key={f.amostra_codigo}>
                <NomeDaAmostra emLinha
                  amostra={{ codigo: f.amostra_codigo, identificacao: f.identificacao }} />
                {' '}→ «{f.identificacao_prevista ?? 'sem nome'}»
                {f.herdados.length > 0
                  ? <> · programados: {f.herdados.join(', ')}</>
                  : <> · sem ensaio marcado na prevista</>}
                {f.nao_entraram.length > 0 && (
                  <span className="aviso-linha">
                    {' '}· não entraram: {f.nao_entraram.join(', ')} (veja o saldo do escopo)
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Aviso>
      )}

      {!nada && (
        <>
          <div className="lista ligar-previstas-lista">
            {soltas.map((s) => {
              const atual = escolha[s.id] ?? '';
              const tomadas = new Set(
                Object.entries(escolha).filter(([a, p]) => a !== s.id && p).map(([, p]) => p));
              const opcoes = dados.abertas.filter((p) => !tomadas.has(p.id));
              const prevista = dados.abertas.find((p) => p.id === atual);
              return (
                <div key={s.id} className="item ligar-linha">
                  <div className="ligar-amostra">
                    {/* 303 (RN-248): o nome do cliente em destaque — é ele que
                        casa com a prevista —, o código embaixo. */}
                    <NomeDaAmostra amostra={daApi(s)} />
                    {!s.identificacao_recebida
                      && <span className="sub"><span className="nada">sem identificação</span></span>}
                    {!loteId && s.lote_codigo && <span className="meta">recebimento {s.lote_codigo}</span>}
                  </div>
                  <div className="ligar-prevista">
                    {s.ensaio_comecou ? (
                      <span className="meta">
                        Um ensaio desta amostra já começou: a ligação só vale antes disso.
                      </span>
                    ) : (
                      <>
                        <label className="so-leitor" htmlFor={`ligar-${s.id}`}>
                          Prevista para {nomeDaAmostra(daApi(s))}
                        </label>
                        <select
                          id={`ligar-${s.id}`}
                          value={atual}
                          onChange={(e) => setEscolha((x) => ({ ...x, [s.id]: e.target.value }))}
                        >
                          <option value="">— deixar sem ligação —</option>
                          {opcoes.map((p) => (
                            <option key={p.id} value={p.id}>{nomeDaPrevista(p)}</option>
                          ))}
                        </select>
                        <span className="meta">
                          {prevista
                            ? <>
                                {sugerida.get(s.id) === prevista.id && 'sugerida pelo nome · '}
                                {prevista.metodos.length > 0
                                  ? `traz ${prevista.metodos.join(', ')}`
                                  : 'sem ensaio marcado'}
                              </>
                            : 'nenhuma prevista escolhida'}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="linha ligar-acoes">
            <button
              type="button" className="botao"
              disabled={ocupado || pares.length === 0}
              onClick={ligar}
            >
              {ocupado
                ? 'Ligando…'
                : pares.length === 0
                  ? 'Escolha a prevista de cada amostra'
                  : `Ligar ${plural(pares.length, 'amostra', 'amostras')}`}
            </button>
          </div>
        </>
      )}
    </>
  );

  if (!cartao) return <div className="ligar-previstas">{corpo}</div>;

  return (
    <section className="cartao ligar-previstas">
      <h2>Ligar às previstas da OS</h2>
      <p className="sub">
        Estas amostras chegaram sem ligação com o que a OS previu. Ligar traz os
        ensaios marcados na prevista e tira a OS de «Esperando amostra».
      </p>
      {corpo}
    </section>
  );
}
